import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/utils/cn';
export interface CalendarProps {
  selected?: Date;
  onSelect?: (date: Date) => void;
  disabled?: (date: Date) => boolean;
  mode?: 'single' | 'multiple';
  className?: string;
  showOutsideDays?: boolean;
}

export const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
  (
    { selected, onSelect, disabled, mode: _mode = 'single', className, showOutsideDays = true },
    ref
  ) => {
    const [currentMonth, setCurrentMonth] = useState(selected || new Date());

    const today = new Date();
    const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startDate = new Date(monthStart);
    startDate.setDate(startDate.getDate() - monthStart.getDay());

    const endDate = new Date(monthEnd);
    endDate.setDate(endDate.getDate() + (6 - monthEnd.getDay()));

    const days = [];
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      days.push(new Date(date));
    }

    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const navigateMonth = (direction: 'prev' | 'next') => {
      const newMonth = new Date(currentMonth);
      if (direction === 'prev') {
        newMonth.setMonth(newMonth.getMonth() - 1);
      } else {
        newMonth.setMonth(newMonth.getMonth() + 1);
      }
      setCurrentMonth(newMonth);
    };

    const isSameDay = (date1: Date, date2?: Date) => {
      if (!date2) return false;
      return (
        date1.getDate() === date2.getDate() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getFullYear() === date2.getFullYear()
      );
    };

    const isToday = (date: Date) => isSameDay(date, today);
    const isSelected = (date: Date) => selected && isSameDay(date, selected);
    const isCurrentMonth = (date: Date) => date.getMonth() === currentMonth.getMonth();
    const isDisabled = (date: Date) => disabled && disabled(date);

    const handleDateClick = (date: Date) => {
      if (isDisabled(date) || !onSelect) return;
      onSelect(date);
    };

    return (
      <div ref={ref} className={cn('p-3 bg-white border rounded-lg shadow-sm', className)}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={() => navigateMonth('prev')}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="font-medium text-sm">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </div>
          <button
            type="button"
            onClick={() => navigateMonth('next')}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Day names */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div key={day} className="p-2 text-xs font-medium text-gray-500 text-center">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1">
          {days.map((day, index) => {
            const isOutsideMonth = !isCurrentMonth(day);
            const shouldShow = showOutsideDays || !isOutsideMonth;

            if (!shouldShow) {
              return <div key={index} className="p-2" />;
            }

            return (
              <button
                key={index}
                type="button"
                onClick={() => handleDateClick(day)}
                disabled={isDisabled(day)}
                className={cn(
                  'p-2 text-sm font-normal relative transition-colors rounded-md',
                  'hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
                  isOutsideMonth && 'text-gray-400',
                  !isOutsideMonth && 'text-gray-900',
                  isToday(day) && 'bg-gray-100 font-medium',
                  isSelected(day) && 'bg-red-600 text-white hover:bg-red-700',
                  isDisabled(day) && 'opacity-50 cursor-not-allowed hover:bg-transparent'
                )}
              >
                {day.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
);

Calendar.displayName = 'Calendar';
